/**
 * Enums compartidos entre backend y frontend.
 * Fuente de verdad: este archivo. Prisma replica los mismos literales.
 *
 * Patrón "const + type" en lugar de `enum` para mejor tree-shaking,
 * compatibilidad con `erasableSyntaxOnly` y serialización JSON directa.
 */

export const Rol = {
  ESTUDIANTE: "ESTUDIANTE",
  BACHILLER: "BACHILLER",
  // ADR 054 — DOCENTE es el rol base (mayor) de la planta. "Ser asesor" / "ser
  // jurado" NO son roles: son CAPACIDADES (flags `puedeAsesorar`/`puedeSerJurado`
  // en `PerfilDocente`) que otorga el ADMIN. Los antiguos roles ASESOR y JURADO
  // se removieron en el cutover del ADR 054.
  DOCENTE: "DOCENTE",
  DECANATO: "DECANATO",
  REPOSITORIO: "REPOSITORIO",
  ADMIN: "ADMIN",
  // ADR 045 — Rol de PLATAFORMA (cross-tenant), no institucional. Único que crea
  // y lista instituciones (tenants). No opera sobre expedientes; vive fuera del
  // aislamiento por institución (institucionId null).
  SUPER_ADMIN: "SUPER_ADMIN",
} as const;
export type Rol = (typeof Rol)[keyof typeof Rol];

/**
 * Capacidades del docente (ADR 054). Son funciones que el ADMIN otorga sobre el
 * rol base `DOCENTE` (flags `puedeAsesorar`/`puedeSerJurado` en `PerfilDocente`),
 * no roles de usuario. Se usan para filtrar la búsqueda de docentes elegibles
 * (combobox de designación de asesor / jurado).
 */
export const CapacidadDocente = {
  ASESOR: "asesor",
  JURADO: "jurado",
} as const;
export type CapacidadDocente = (typeof CapacidadDocente)[keyof typeof CapacidadDocente];

export const EstadoUsuario = {
  ACTIVO: "ACTIVO",
  INACTIVO: "INACTIVO",
  SUSPENDIDO: "SUSPENDIDO",
} as const;
export type EstadoUsuario = (typeof EstadoUsuario)[keyof typeof EstadoUsuario];

/**
 * Estado de una invitación de usuario (ADR 053). El SUPER_ADMIN invita al primer
 * ADMIN de una institución; el ADMIN invitará al resto. La invitación nace
 * `PENDIENTE`, pasa a `ACEPTADA` cuando el invitado define su contraseña, y puede
 * quedar `REVOCADA` si la plataforma la anula antes de usarse. La expiración NO es
 * un estado: se deriva de `expiraEn < now` para no requerir un job que mute filas.
 */
export const EstadoInvitacion = {
  PENDIENTE: "PENDIENTE",
  ACEPTADA: "ACEPTADA",
  REVOCADA: "REVOCADA",
} as const;
export type EstadoInvitacion = (typeof EstadoInvitacion)[keyof typeof EstadoInvitacion];

export const EstadoExpediente = {
  BORRADOR_SOLICITUD: "BORRADOR_SOLICITUD",
  // Handshake estudiante-asesor (Fase 6 — ADR 008)
  ASESORIA_SOLICITADA: "ASESORIA_SOLICITADA",
  ASESORIA_ACEPTADA: "ASESORIA_ACEPTADA",
  SOLICITUD_PRESENTADA: "SOLICITUD_PRESENTADA",
  SOLICITUD_EN_REVISION_DECANATO: "SOLICITUD_EN_REVISION_DECANATO",
  SOLICITUD_OBSERVADA: "SOLICITUD_OBSERVADA",
  SOLICITUD_RECHAZADA: "SOLICITUD_RECHAZADA",
  ASESOR_DESIGNADO: "ASESOR_DESIGNADO",
  PROYECTO_EN_ELABORACION: "PROYECTO_EN_ELABORACION",
  PROYECTO_EN_REVISION_ASESOR: "PROYECTO_EN_REVISION_ASESOR",
  PROYECTO_CON_CONFORMIDAD_ASESOR: "PROYECTO_CON_CONFORMIDAD_ASESOR",
  JURADO_DESIGNADO: "JURADO_DESIGNADO",
  PROYECTO_EN_REVISION_JURADO: "PROYECTO_EN_REVISION_JURADO",
  PROYECTO_OBSERVADO: "PROYECTO_OBSERVADO",
  PROYECTO_RECHAZADO: "PROYECTO_RECHAZADO",
  PROYECTO_APROBADO: "PROYECTO_APROBADO",
  TESIS_EN_EJECUCION: "TESIS_EN_EJECUCION",
  PLAZO_EJECUCION_VENCIDO: "PLAZO_EJECUCION_VENCIDO",
  BORRADOR_TESIS_PRESENTADO: "BORRADOR_TESIS_PRESENTADO",
  BORRADOR_EN_REVISION_JURADO: "BORRADOR_EN_REVISION_JURADO",
  BORRADOR_OBSERVADO: "BORRADOR_OBSERVADO",
  BORRADOR_SUBSANADO: "BORRADOR_SUBSANADO",
  BORRADOR_INSUFICIENTE: "BORRADOR_INSUFICIENTE",
  BORRADOR_SUFICIENTE: "BORRADOR_SUFICIENTE",
  EN_CONTROL_ANTIPLAGIO: "EN_CONTROL_ANTIPLAGIO",
  ANTIPLAGIO_OBSERVADO: "ANTIPLAGIO_OBSERVADO",
  ANTIPLAGIO_APROBADO: "ANTIPLAGIO_APROBADO",
  FECHA_SUSTENTACION_SOLICITADA: "FECHA_SUSTENTACION_SOLICITADA",
  SUSTENTACION_PROGRAMADA: "SUSTENTACION_PROGRAMADA",
  SUSTENTACION_REPROGRAMADA: "SUSTENTACION_REPROGRAMADA",
  SUSTENTACION_REALIZADA: "SUSTENTACION_REALIZADA",
  SUSTENTACION_APROBADA: "SUSTENTACION_APROBADA",
  SUSTENTACION_DESAPROBADA: "SUSTENTACION_DESAPROBADA",
  TESIS_REGISTRADA_REPOSITORIO: "TESIS_REGISTRADA_REPOSITORIO",
  TITULO_EN_TRAMITE: "TITULO_EN_TRAMITE",
  TITULO_OTORGADO: "TITULO_OTORGADO",
  EXPEDIENTE_OBSERVADO: "EXPEDIENTE_OBSERVADO",
  TRAMITE_SUSPENDIDO: "TRAMITE_SUSPENDIDO",
} as const;
export type EstadoExpediente = (typeof EstadoExpediente)[keyof typeof EstadoExpediente];

export const EstadoSolicitud = {
  PRESENTADA: "PRESENTADA",
  EN_REVISION: "EN_REVISION",
  OBSERVADA: "OBSERVADA",
  APROBADA: "APROBADA",
  RECHAZADA: "RECHAZADA",
} as const;
export type EstadoSolicitud = (typeof EstadoSolicitud)[keyof typeof EstadoSolicitud];

export const TipoResolucion = {
  DESIGNACION_ASESOR: "DESIGNACION_ASESOR",
  DESIGNACION_JURADO: "DESIGNACION_JURADO",
  APROBACION_PROYECTO: "APROBACION_PROYECTO",
  // ADR 015 — Bloque 5.
  APROBACION_INFORME: "APROBACION_INFORME",
  FECHA_SUSTENTACION: "FECHA_SUSTENTACION",
  OTORGAMIENTO_TITULO: "OTORGAMIENTO_TITULO",
} as const;
export type TipoResolucion = (typeof TipoResolucion)[keyof typeof TipoResolucion];

export const EstadoResolucion = {
  EMITIDA: "EMITIDA",
  ANULADA: "ANULADA",
} as const;
export type EstadoResolucion = (typeof EstadoResolucion)[keyof typeof EstadoResolucion];

export const TipoDocumento = {
  SOLICITUD_FORMAL: "SOLICITUD_FORMAL",
  CARTA_ACEPTACION_ASESOR: "CARTA_ACEPTACION_ASESOR",
  PROPUESTA_TITULO: "PROPUESTA_TITULO",
  PROYECTO_TESIS: "PROYECTO_TESIS",
  INFORME_CONFORMIDAD_ASESOR: "INFORME_CONFORMIDAD_ASESOR",
  RESOLUCION_DESIGNACION_ASESOR: "RESOLUCION_DESIGNACION_ASESOR",
  RESOLUCION_DESIGNACION_JURADO: "RESOLUCION_DESIGNACION_JURADO",
  INFORME_COLEGIADO: "INFORME_COLEGIADO",
  RESOLUCION_APROBACION_PROYECTO: "RESOLUCION_APROBACION_PROYECTO",
  BORRADOR_TESIS: "BORRADOR_TESIS",
  DICTAMEN_SUFICIENCIA: "DICTAMEN_SUFICIENCIA",
  // ADR 015 — Bloque 5: resolución decanal de aprobación del informe final.
  // Se emite después de que el jurado cierra el DICTAMEN_SUFICIENCIA con
  // veredicto SUFICIENTE; el Decanato formaliza institucionalmente el
  // cierre del ciclo del borrador. Reusa el layout RESOLUCION (DRY).
  RESOLUCION_APROBACION_INFORME: "RESOLUCION_APROBACION_INFORME",
  REPORTE_SIMILITUD: "REPORTE_SIMILITUD",
  RESOLUCION_FECHA_SUSTENTACION: "RESOLUCION_FECHA_SUSTENTACION",
  ACTA_INDIVIDUAL: "ACTA_INDIVIDUAL",
  TESIS_FINAL: "TESIS_FINAL",
  CONSTANCIA_REPOSITORIO: "CONSTANCIA_REPOSITORIO",
  EXPEDIENTE_TITULACION: "EXPEDIENTE_TITULACION",
  CONSTANCIA_TITULO: "CONSTANCIA_TITULO",
} as const;
export type TipoDocumento = (typeof TipoDocumento)[keyof typeof TipoDocumento];

export const EstadoDocumento = {
  VIGENTE: "VIGENTE",
  HISTORICA: "HISTORICA",
  ELIMINADA: "ELIMINADA",
} as const;
export type EstadoDocumento = (typeof EstadoDocumento)[keyof typeof EstadoDocumento];

/**
 * Origen del documento (Fase 6 — ADR 007).
 *
 * - UPLOAD: archivo subido por un actor (modelo histórico, fallback).
 * - GENERADO: emitido por el sistema con plantilla + hash + QR (canónico).
 *
 * El landing público de verificación (GET /verificar/:anchor) solo
 * reconoce documentos GENERADO.
 */
export const OrigenDocumento = {
  UPLOAD: "UPLOAD",
  GENERADO: "GENERADO",
} as const;
export type OrigenDocumento = (typeof OrigenDocumento)[keyof typeof OrigenDocumento];

/**
 * Estado del handshake explícito estudiante-asesor (Fase 6 — ADR 008).
 *
 * - PENDIENTE: el estudiante propuso al asesor, esperando respuesta.
 * - ACEPTADA: el asesor aceptó (sistema generó la CARTA_ACEPTACION_ASESOR).
 * - RECHAZADA: el asesor rechazó (con motivo); el expediente vuelve a
 *   BORRADOR_SOLICITUD y el estudiante puede reintentar con otro asesor.
 */
export const EstadoAceptacionAsesor = {
  PENDIENTE: "PENDIENTE",
  ACEPTADA: "ACEPTADA",
  RECHAZADA: "RECHAZADA",
} as const;
export type EstadoAceptacionAsesor =
  (typeof EstadoAceptacionAsesor)[keyof typeof EstadoAceptacionAsesor];

export const RolJurado = {
  PRESIDENTE: "PRESIDENTE",
  SECRETARIO: "SECRETARIO",
  VOCAL: "VOCAL",
  ACCESITARIO: "ACCESITARIO",
} as const;
export type RolJurado = (typeof RolJurado)[keyof typeof RolJurado];

/**
 * Resultado del informe colegiado del jurado sobre el proyecto de tesis.
 *
 * Nota: "PENDIENTE" no aparece aquí porque no se persiste — la ausencia de
 * una fila `RevisionProyecto` para un (expediente, miembroJurado) ya
 * representa ese estado. Mantener los enums alineados con los valores
 * realmente persistidos evita revisores que tienen que adivinar si una
 * fila puede tener PENDIENTE o no.
 */
export const ResultadoRevisionProyecto = {
  APROBADO: "APROBADO",
  OBSERVADO: "OBSERVADO",
  RECHAZADO: "RECHAZADO",
} as const;
export type ResultadoRevisionProyecto =
  (typeof ResultadoRevisionProyecto)[keyof typeof ResultadoRevisionProyecto];

/**
 * Resultado del dictamen de suficiencia del borrador de tesis.
 * Mismo principio que `ResultadoRevisionProyecto`: PENDIENTE = no fila.
 */
export const ResultadoRevisionBorrador = {
  SUFICIENTE: "SUFICIENTE",
  INSUFICIENTE: "INSUFICIENTE",
} as const;
export type ResultadoRevisionBorrador =
  (typeof ResultadoRevisionBorrador)[keyof typeof ResultadoRevisionBorrador];

/**
 * Resultado del control antiplagio (Fase 3 §F3-003).
 *
 * El sistema persiste el resultado calculado al comparar la similitud
 * detectada contra el umbral configurado (default 25%, sobreescribible por
 * escuela en una iteración futura). La ausencia de fila `ReporteSimilitud`
 * implica que el control aún no se ha realizado — no necesitamos un valor
 * "PENDIENTE" en este enum.
 *
 * Renombrado desde `EstadoReporteSimilitud` (Fase 0 reservó el nombre):
 * `ResultadoAntiplagio` deja claro que es el veredicto persistido, no un
 * estado de máquina.
 */
export const ResultadoAntiplagio = {
  APROBADO: "APROBADO",
  OBSERVADO: "OBSERVADO",
} as const;
export type ResultadoAntiplagio = (typeof ResultadoAntiplagio)[keyof typeof ResultadoAntiplagio];

export const EstadoSustentacion = {
  SOLICITADA: "SOLICITADA",
  PROGRAMADA: "PROGRAMADA",
  REALIZADA: "REALIZADA",
  REPROGRAMADA: "REPROGRAMADA",
  CANCELADA: "CANCELADA",
} as const;
export type EstadoSustentacion = (typeof EstadoSustentacion)[keyof typeof EstadoSustentacion];

export const ModalidadSustentacion = {
  PRESENCIAL: "PRESENCIAL",
  VIRTUAL: "VIRTUAL",
  SEMIPRESENCIAL: "SEMIPRESENCIAL",
} as const;
export type ModalidadSustentacion =
  (typeof ModalidadSustentacion)[keyof typeof ModalidadSustentacion];

/**
 * Resultado final de la sustentación tras cerrarla. PENDIENTE no se
 * persiste — la ausencia del campo `resultado` en `Sustentacion`
 * representa ese estado mientras el acto está en curso o programado.
 */
export const ResultadoSustentacion = {
  APROBADO: "APROBADO",
  DESAPROBADO: "DESAPROBADO",
} as const;
export type ResultadoSustentacion =
  (typeof ResultadoSustentacion)[keyof typeof ResultadoSustentacion];

export const EstadoTramiteTitulo = {
  INICIADO: "INICIADO",
  EN_REVISION: "EN_REVISION",
  OBSERVADO: "OBSERVADO",
  APROBADO: "APROBADO",
  OTORGADO: "OTORGADO",
} as const;
export type EstadoTramiteTitulo = (typeof EstadoTramiteTitulo)[keyof typeof EstadoTramiteTitulo];

export const CanalNotificacion = {
  EMAIL: "EMAIL",
  IN_APP: "IN_APP",
  WHATSAPP: "WHATSAPP",
} as const;
export type CanalNotificacion = (typeof CanalNotificacion)[keyof typeof CanalNotificacion];

export const EstadoNotificacion = {
  PENDIENTE: "PENDIENTE",
  ENVIADA: "ENVIADA",
  FALLIDA: "FALLIDA",
  LEIDA: "LEIDA",
} as const;
export type EstadoNotificacion = (typeof EstadoNotificacion)[keyof typeof EstadoNotificacion];

export const TipoEventoAuditoria = {
  CREACION_SOLICITUD: "CREACION_SOLICITUD",
  SUBIDA_DOCUMENTO: "SUBIDA_DOCUMENTO",
  DESIGNACION_ASESOR: "DESIGNACION_ASESOR",
  EMISION_RESOLUCION: "EMISION_RESOLUCION",
  CONFORMIDAD_ASESOR: "CONFORMIDAD_ASESOR",
  DESIGNACION_JURADO: "DESIGNACION_JURADO",
  EMISION_INFORME_COLEGIADO: "EMISION_INFORME_COLEGIADO",
  APROBACION_PROYECTO: "APROBACION_PROYECTO",
  PRESENTACION_BORRADOR: "PRESENTACION_BORRADOR",
  DICTAMEN_SUFICIENCIA: "DICTAMEN_SUFICIENCIA",
  REGISTRO_REPORTE_ANTIPLAGIO: "REGISTRO_REPORTE_ANTIPLAGIO",
  APROBACION_ANTIPLAGIO: "APROBACION_ANTIPLAGIO",
  PROGRAMACION_SUSTENTACION: "PROGRAMACION_SUSTENTACION",
  REGISTRO_CALIFICACION: "REGISTRO_CALIFICACION",
  APROBACION_SUSTENTACION: "APROBACION_SUSTENTACION",
  REGISTRO_REPOSITORIO: "REGISTRO_REPOSITORIO",
  OTORGAMIENTO_TITULO: "OTORGAMIENTO_TITULO",
  TRANSICION_ESTADO: "TRANSICION_ESTADO",
  LOGIN: "LOGIN",
  LOGOUT: "LOGOUT",
  // Fase 6 (handshake + documentos generados)
  SOLICITUD_ASESORIA_CREADA: "SOLICITUD_ASESORIA_CREADA",
  ACEPTACION_ASESORIA: "ACEPTACION_ASESORIA",
  RECHAZO_ASESORIA: "RECHAZO_ASESORIA",
  EMISION_DOCUMENTO_GENERADO: "EMISION_DOCUMENTO_GENERADO",
  // Fase C (editor LaTeX integrado)
  COMPILACION_LATEX_INICIADA: "COMPILACION_LATEX_INICIADA",
  COMPILACION_LATEX_EXITOSA: "COMPILACION_LATEX_EXITOSA",
  COMPILACION_LATEX_CON_ERRORES: "COMPILACION_LATEX_CON_ERRORES",
  COMPILACION_LATEX_TIMEOUT: "COMPILACION_LATEX_TIMEOUT",
  // ADR 019 (rebote estudiante↔asesor sobre proyecto en elaboración)
  ENVIO_PROYECTO_A_REVISION: "ENVIO_PROYECTO_A_REVISION",
  OBSERVACION_PROYECTO_ASESOR: "OBSERVACION_PROYECTO_ASESOR",
  // ADR 054 — el ADMIN otorga/revoca capacidades (puedeAsesorar/puedeSerJurado)
  // a un docente de su institución.
  ASIGNACION_CAPACIDAD_DOCENTE: "ASIGNACION_CAPACIDAD_DOCENTE",
  // ADR 053 — provisioning de usuarios. El SUPER_ADMIN crea la institución + su
  // primer ADMIN por invitación; el invitado activa su cuenta definiendo su clave.
  INVITACION_ADMIN_CREADA: "INVITACION_ADMIN_CREADA",
  CUENTA_ACTIVADA: "CUENTA_ACTIVADA",
  // ADR 055 — el ADMIN invita a un docente / estudiante de su institución.
  INVITACION_DOCENTE_CREADA: "INVITACION_DOCENTE_CREADA",
  INVITACION_ESTUDIANTE_CREADA: "INVITACION_ESTUDIANTE_CREADA",
} as const;
export type TipoEventoAuditoria = (typeof TipoEventoAuditoria)[keyof typeof TipoEventoAuditoria];

export const TipoFeriado = {
  NACIONAL: "NACIONAL",
  INSTITUCIONAL: "INSTITUCIONAL",
  SUSPENSION_ACADEMICA: "SUSPENSION_ACADEMICA",
} as const;
export type TipoFeriado = (typeof TipoFeriado)[keyof typeof TipoFeriado];
